import { compact } from 'lodash'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Chip, Grid, Paper, Stack, Typography } from '@mui/material'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'

import colors from '@/assets/scss/_themes-vars.module.scss'
import InfoSection from '@/components/ui/sections/InfoSection'
import { SavedAddress } from '@/core/types/shared'

type SavedAddressesSectionProps = {
  savedAddresses?: SavedAddress[]
}

/**
 * Read-only view of the addresses a patient saved from the patient app. These are distinct from the
 * patient's legacy top-level address (see AddressInfoSection); the default one is mirrored onto it.
 */
export function SavedAddressesSection({ savedAddresses }: SavedAddressesSectionProps) {
  useLingui()

  if (!savedAddresses?.length) return null

  // Show the default first, then keep the stored order.
  const ordered = [...savedAddresses].sort((a, b) => Number(b.isDefault) - Number(a.isDefault))

  return (
    <InfoSection label={t`Saved addresses`} description={t`Addresses the patient saved in the app. Read-only.`}>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {ordered.map((address) => (
          <SavedAddressCard key={address._id} address={address} />
        ))}
      </Stack>
    </InfoSection>
  )
}

function SavedAddressCard({ address }: { address: SavedAddress }) {
  const addressLine = compact([address.street, compact([address.postalCode, address.city]).join(' '), address.country]).join(', ')

  const details = compact([
    address.apartment && `${t`Apartment`}: ${address.apartment}`,
    address.floor && `${t`Floor`}: ${address.floor}`,
    address.doorCode && `${t`Door code`}: ${address.doorCode}`,
  ]).join(' · ')

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HomeOutlinedIcon sx={{ fontSize: 18, color: colors.gray500 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.gray900 }}>
              {address.label}
            </Typography>
            {address.isDefault && <Chip size="small" color="primary" label={t`Default`} sx={{ height: 20 }} />}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: colors.gray900 }}>
            {addressLine}
          </Typography>
        </Grid>
        {details && (
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: colors.gray500 }}>
              {details}
            </Typography>
          </Grid>
        )}
        {address.instructions && (
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: colors.gray500 }}>
              <Trans>Instructions</Trans>: {address.instructions}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}
